import { FC, ReactNode } from 'react';
import { Box, Card, CardActionArea, IconButton, Stack, SxProps, Typography, useTheme } from '@mui/material';
import { ThemeMode } from '../../../../types/config';
import { CreateOutlined, Link, LinkOff } from '@mui/icons-material';
import { getNightOrDayPalette } from '../../../../utils/getNightOrDayPalette';
import { Editor } from '../../../../components/wysiwyg/Editor';
import { Theme } from '@mui/material/styles';

type CellCardProps = Readonly<{
	name: string;
	description: string;
	onEdit: () => void;
	onRemove: () => void;
	icon: (props: { sx: SxProps<Theme> }) => ReactNode;
	isActive: boolean;
	disabled: boolean;
	backgroundColor?: string;
}>;

export const CellCard: FC<CellCardProps> = ({ name, description, onEdit, onRemove, icon, isActive, disabled, backgroundColor }) => {
	const theme = useTheme();

	return (
		<Card
			variant={'outlined'}
			sx={{
				...(theme.palette.mode === ThemeMode.DARK
					? {
							backgroundColor: theme.palette.night[100],
							borderColor: isActive ? theme.palette.primary.main : 'transparent',
						}
					: {
							borderColor: isActive ? theme.palette.primary.main : theme.palette.day[300],
						}),
				'&:not(:hover) .cell-card-buttons-hover': {
					display: 'none',
				},
				'&:hover .cell-card-buttons-icon': {
					display: 'none',
				},
				borderRadius: '6px',
				boxShadow: '0px 2px 0px 0px #00000004',
				...(backgroundColor && { backgroundColor }),
			}}
		>
			<CardActionArea onClick={onEdit}>
				<Box sx={{ padding: '8px 6px 8px 12px' }}>
					<Stack direction={'column'}>
						<Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
							<Typography variant={'h6'} fontWeight={500} noWrap>
								{name}
							</Typography>
							<IconButton className={'cell-card-buttons-icon'}>
								{icon({
									sx: { fill: isActive ? theme.palette.primary.main : theme.palette[getNightOrDayPalette(theme)][500], fontSize: '20px' },
								})}
							</IconButton>
							<Stack direction={'row'} className={'cell-card-buttons-hover'}>
								<IconButton
									onClick={(e) => {
										e.stopPropagation();
										onRemove();
									}}
									sx={{ '&:not(:hover) > :nth-child(2)': { display: 'none' }, '&:hover > :first-of-type': { display: 'none' } }}
									disabled={disabled}
								>
									<Link sx={{ fill: theme.palette[getNightOrDayPalette(theme)][500], fontSize: '20px' }} />
									<LinkOff sx={{ fill: theme.palette[getNightOrDayPalette(theme)]['A100'], fontSize: '20px' }} />
								</IconButton>
								<IconButton onClick={onEdit} disabled={disabled}>
									<CreateOutlined
										sx={{ fill: isActive ? theme.palette.primary.main : theme.palette[getNightOrDayPalette(theme)][500], fontSize: '20px' }}
									/>
								</IconButton>
							</Stack>
						</Stack>
						{description && (
							<Typography
								variant={'body2'}
								sx={{
									'& > div': {
										color: theme.palette[getNightOrDayPalette(theme)][600],
										marginTop: -0.75,
										// NOTE: parent's padding is uneven
										paddingRight: '6px',
										display: '-webkit-box',
										overflow: 'hidden',
										WebkitBoxOrient: 'vertical',
										WebkitLineClamp: 2,
									},
								}}
							>
								<Editor readonly state={description} />
							</Typography>
						)}
					</Stack>
				</Box>
			</CardActionArea>
		</Card>
	);
};
